import axios, { AxiosError, AxiosInstance } from 'axios';
import { ApiError } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  private api: AxiosInstance;

  private getCSRFToken(): string | null {
    // First check sessionStorage (works across domains)
    const sessionToken = sessionStorage.getItem('csrfToken');
    if (sessionToken) {
      return sessionToken;
    }
    
    // Fall back to cookies (for same-domain scenarios)
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === '_csrf') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add cache-control headers to prevent stale data
        config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        config.headers['Pragma'] = 'no-cache';
        config.headers['Expires'] = '0';
        
        // Add CSRF token to all non-GET requests
        if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
          const csrfToken = this.getCSRFToken();
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as any;
        
        // Handle CSRF token errors
        if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_ERROR') {
          // Prevent infinite loop
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
              // Refresh CSRF token
              const authService = (await import('./auth')).default;
              await authService.refreshCSRFToken();
              
              // Retry the original request
              return this.api(originalRequest);
            } catch (refreshError) {
              console.error('Failed to refresh CSRF token:', refreshError);
              // If refresh fails, continue with original error
            }
          }
        }
        
        if (error.response) {
          // Handle specific error codes
          const errorCode = error.response.data?.code;
          
          // Only redirect to login if we're not already on a public page
          const publicPaths = ['/', '/landing', '/login', '/signup', '/verify-email', '/forgot-password', '/reset-password'];
          const currentPath = window.location.pathname;
          
          if ((errorCode === 'AUTH_002' || errorCode === 'AUTH_003') && !publicPaths.includes(currentPath)) {
            // Token expired or invalid - only redirect if not on a public page
            // Don't redirect if we're checking auth status from the root path
            if (currentPath !== '/') {
              window.location.href = '/login';
            }
          }
          
          // Handle access denied due to tier mismatch
          if (errorCode === 'BIZ_002' && error.response.status === 403) {
            // Trigger a page reload after a short delay to refresh catalog
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  get<T>(url: string, params?: any) {
    return this.api.get<T>(url, { params });
  }

  post<T>(url: string, data?: any) {
    return this.api.post<T>(url, data);
  }

  put<T>(url: string, data?: any) {
    return this.api.put<T>(url, data);
  }

  delete<T>(url: string) {
    return this.api.delete<T>(url);
  }

  uploadFiles<T>(url: string, formData: FormData) {
    return this.api.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

export default new ApiService();